/**
 * Types for PKA Ton Concours - Concours Management
 * Based on API_CONCOURS_CRUD.md specifications
 */

// ============================================
// Enums and Constants
// ============================================

export type ConcoursStatus =
  | 'draft'
  | 'upcoming'
  | 'open'
  | 'registration_closed'
  | 'in_progress'
  | 'completed'
  | 'cancelled';

export type ConcoursCategory =
  | 'Éducation'
  | 'Santé'
  | 'Sécurité'
  | 'Administration'
  | 'Économie';

export type ConcoursLevel =
  | 'BEPC'
  | 'BAC'
  | 'BAC+2'
  | 'Licence'
  | 'Master'
  | 'Doctorat'
  | 'Professionnel'
  | 'Autre';

export type ConcoursType =
  | 'direct'
  | 'professionnel'
  | 'interne';

export const CONCOURS_STATUS_LABELS: Record<ConcoursStatus, string> = {
  draft: 'Brouillon',
  upcoming: 'À venir',
  open: 'Inscriptions ouvertes',
  registration_closed: 'Inscriptions fermées',
  in_progress: 'En cours',
  completed: 'Terminé',
  cancelled: 'Annulé'
};

export const CONCOURS_STATUS_COLORS: Record<ConcoursStatus, string> = {
  draft: 'bg-gray-100 text-gray-800',
  upcoming: 'bg-blue-100 text-blue-800',
  open: 'bg-green-100 text-green-800',
  registration_closed: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-gray-100 text-gray-600',
  cancelled: 'bg-red-100 text-red-800'
};

export const CONCOURS_CATEGORIES: ConcoursCategory[] = [
  'Éducation',
  'Santé',
  'Sécurité',
  'Administration',
  'Économie'
];

export const CONCOURS_LEVELS: ConcoursLevel[] = [
  'BEPC',
  'BAC',
  'BAC+2',
  'Licence',
  'Master',
  'Doctorat',
  'Professionnel',
  'Autre'
];

// ============================================
// Sub-Types (JSONB fields)
// ============================================

export interface RequiredDiploma {
  name: string;
  full_name: string;
  alternatives?: string[];
}

export interface Eligibility {
  nationality: string;
  min_age: number;
  max_age: number;
  age_reference_date?: string;
  age_note?: string;
  required_diplomas: RequiredDiploma[];
  diploma_restrictions?: string[];
  other_conditions?: string[];
}

export interface DocumentItem {
  name: string;
  type: 'original' | 'copie' | 'imprimé' | 'fourniture';
  validity?: string;
  note?: string;
  source?: string;
  amount?: number;
  payment?: string;
}

export interface RequiredDocumentCategory {
  category: string;
  documents: DocumentItem[];
}

export interface CalendarEvent {
  id?: string;
  label: string;
  start?: string;
  end?: string;
  date?: string;
  type?: 'period' | 'date'; // Période avec début/fin ou date unique
}

export interface Calendar {
  events: CalendarEvent[]; // Liste dynamique d'événements
}

export interface MedicalExamSite {
  city: string;
  location: string;
  exam_period: {
    start: string;
    end: string;
  };
  results_period?: {
    start: string;
    end: string;
  };
}

export interface MedicalExam {
  required: boolean;
  provider: string;
  full_name?: string;
  website?: string;
  fee: number;
  payment_methods?: string[];
  registration_period?: {
    start: string;
    end: string;
  };
  sites?: MedicalExamSite[];
  exams_to_pass?: string[];
  instructions?: {
    before?: string[];
    during?: string[];
    after?: string[];
  };
}

export interface RegistrationStep {
  order: number;
  name: string;
  period?: {
    start: string;
    end: string;
  };
  website?: string;
  location?: string;
  substeps?: string[];
  objective?: string;
  warning?: string;
}

export interface OtherFee {
  name: string;
  amount: number;
  note?: string;
}

export interface ExamInfo {
  type: 'written' | 'oral' | 'physical' | 'practical';
  name: string;
  date?: string;
  note?: string;
  duration?: string;
  subjects?: string[];
  coefficient?: number;
}

export interface SubmissionLocation {
  type: string;
  purpose: string;
  note?: string;
}

export interface Links {
  official?: string;
  registration?: string;
  medical_exam?: string;
  results?: string;
  more_info?: string;
}

export interface Contact {
  type: 'phone' | 'whatsapp' | 'email' | 'address';
  label?: string;
  value: string;
  hours?: string;
}

// ============================================
// Main Concours Model
// ============================================

export interface Concours {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  institution: string;
  organizing_body?: string;
  year: number;

  // Classification
  category: ConcoursCategory;
  subcategory?: string;
  level: ConcoursLevel;
  type?: ConcoursType;
  is_regionalized?: boolean;

  // JSONB fields
  eligibility?: Eligibility;
  required_documents?: RequiredDocumentCategory[];
  calendar?: Calendar;
  medical_exam?: MedicalExam;
  registration_steps?: RegistrationStep[];
  exams?: ExamInfo[];
  submission_locations?: SubmissionLocation[];
  links?: Links;
  contacts?: Contact[];

  // Fees
  registration_fee?: number;
  medical_exam_fee?: number;
  other_fees?: OtherFee[];
  payment_methods?: string[];

  // Status & Visibility
  status: ConcoursStatus;
  is_featured?: boolean;
  is_popular?: boolean;
  priority?: number;

  // Media
  image_url?: string;
  logo_url?: string;
  banner_url?: string;

  // SEO & Search
  keywords?: string[];
  tips?: string[];
  warnings?: string[];

  // Metrics
  view_count?: number;
  save_count?: number;

  // Timestamps
  created_at?: string;
  updated_at?: string;
  published_at?: string;
  last_verified_at?: string;

  // Edition management
  concours_code?: string;
  edition_number?: number;
  parent_concours_id?: string;
  source?: string;
  source_url?: string;
}

// ============================================
// API Request/Response Types
// ============================================

export interface ConcoursCreateRequest {
  slug: string;
  title: string;
  subtitle?: string;
  description?: string;
  institution: string;
  organizing_body?: string;
  year: number;
  category: ConcoursCategory;
  subcategory?: string;
  level: ConcoursLevel;
  type?: ConcoursType;
  is_regionalized?: boolean;
  eligibility?: Eligibility;
  required_documents?: RequiredDocumentCategory[];
  calendar?: Calendar;
  medical_exam?: MedicalExam;
  registration_steps?: RegistrationStep[];
  exams?: ExamInfo[];
  submission_locations?: SubmissionLocation[];
  links?: Links;
  contacts?: Contact[];
  registration_fee?: number;
  medical_exam_fee?: number;
  other_fees?: OtherFee[];
  payment_methods?: string[];
  status?: ConcoursStatus;
  is_featured?: boolean;
  is_popular?: boolean;
  priority?: number;
  image_url?: string;
  logo_url?: string;
  banner_url?: string;
  keywords?: string[];
  tips?: string[];
  warnings?: string[];
}

export type ConcoursUpdateRequest = Partial<ConcoursCreateRequest>;

export interface ConcoursListParams {
  page?: number;
  limit?: number;
  status?: ConcoursStatus;
  category?: ConcoursCategory;
  level?: ConcoursLevel;
  year?: number;
  search?: string;
  is_featured?: boolean;
  is_popular?: boolean;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface ConcoursListResponse {
  success: boolean;
  data: Concours[];
  pagination: PaginationInfo;
  filters_applied?: Record<string, any>;
}

export interface ConcoursResponse {
  success: boolean;
  data: Concours;
  message?: string;
}

export interface ConcoursStatsResponse {
  success: boolean;
  data: {
    total: number;
    by_status: Record<ConcoursStatus, number>;
    by_category: Record<ConcoursCategory, number>;
    featured: number;
    popular: number;
    most_viewed: Array<{ id: string; title: string; views: number }>;
    most_saved: Array<{ id: string; title: string; saves: number }>;
  };
}

export interface ConcoursDuplicateRequest {
  new_slug: string;
  new_year: number;
  status?: ConcoursStatus;
  updates?: Partial<ConcoursCreateRequest>;
}
